import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import classNames from 'classnames';
import { AtlSelectAsyncProps, AtlSelectOption } from './props';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import accessibilityHelper from '../../../accessibilityHelper';
import { getGlobalStyleSheets } from '../../../global-styles';
import {
   Sizes,
   enumAttributeConverter,
   SizesValuesMap,
} from '../../enum-types';
import { SpinnerSize, SpinnerAlignment, SpinnerVariant } from '../../spinner';
import * as TagNames from './select.tagnames';
import {
   AtlSelectChangeEvent,
   AtlSelectKeyDownEvent,
   AtlSelectPreviousSearchEvent,
} from './select.events';

@customElement(TagNames.SelectAsync)
export class AtlSelectAsync extends LitElement implements AtlSelectAsyncProps {
   declare internalId: string;
   declare name: string;
   declare size?: Sizes;
   declare placeholder?: string;
   declare selectClassName?: string;
   declare selectIconName?: string;
   declare listClassName?: string;
   declare selectValue: string;
   declare options: Array<AtlSelectOption> | undefined;
   declare minimumCharacterLength?: number;
   declare selectInactive?: boolean;
   declare showPreviousSearchHistory?: boolean;
   declare clearSearchValue?: boolean;
   declare showToggleOnSelection?: boolean;
   declare protected loading: boolean;
   declare protected toggled: boolean;
   declare protected value: string;
   declare protected showingPreviousSearch: boolean;

   static properties = {
      internalId: { type: String },
      selectClassName: { type: String },
      selectIconName: { type: String },
      listClassName: { type: String },
      name: { type: String },
      placeholder: { type: String },
      size: {
         type: String,
         convertor: enumAttributeConverter(Sizes, Sizes.Medium),
      },
      selectValue: { type: String },
      options: { type: Array },
      minimumCharacterLength: { type: Number },
      selectInactive: { type: Boolean },
      showPreviousSearchHistory: { type: Boolean },
      clearSearchValue: { type: Boolean },
      loading: { type: Boolean, state: true },
      toggled: { type: Boolean, state: true },
      value: { type: String, state: true },
      showingPreviousSearch: { type: String, state: true },
      showToggleOnSelection: { type: String },
      ariaLabel: {type: String }
   };

   constructor() {
      super();
      this.selectClassName = '';
      this.selectIconName = '';
      this.listClassName = '';
      this.name = '';
      this.options = [];
      this.value = '';
   }

   static styles = [
      ...getGlobalStyleSheets(),
      css`
         .dropdown-item:hover {
            cursor: pointer;
         }
         .atl-wc-dropdown-item-text {
            word-wrap: break-word;
            white-space: normal;
            word-break: break-word;
         }
      `,
   ];

   renderOptionsToShow() {
      if (this.options) {
         this.loading = false;
         if (this.options.length > 0) {
            return html`${this.options.map((o) => this.renderOption(o))}`;
         }
      }
      if (this.loading) {
         return html`
            <atlantis-inline-loader
               size=${SpinnerSize.small}
               align=${SpinnerAlignment.TopCenter}
               color=${SpinnerVariant.Primary}
            >
            </atlantis-inline-loader>
         `;
      }

      return html`
         <li
            class=${classNames(
               'dropdown-item p-2 form-select',
               SizesValuesMap[this.size ?? Sizes.Medium]
            )}
            data-type="user-profile-menu-item"
         >
            <slot></slot>
         </li>
      `;
   }

   renderIcon(option: AtlSelectOption) {
      if (!option.icon) return nothing;

      return html`
         <i
            tabindex="-1"
            class=${`fa-solid fa-${option.icon} ${option.iconClassName}`}
         ></i>
      `;
   }

   renderOption(option: AtlSelectOption) {
      return html`
         <li
            class=${classNames(
               'dropdown-item p-2 form-select',
               SizesValuesMap[this.size ?? Sizes.Medium]
            )}
            data-type="user-profile-menu-item"
            tabindex="0"
            @click=${() => this._handleSelection(option.value)}
            @keydown=${(e: KeyboardEvent) =>
               accessibilityHelper.onKeyDownWrapper(e, () =>
                  this._handleItemKeyDown(e, option.value)
               )}
         >
            <div>
               ${this.renderIcon(option)} ${html`${unsafeHTML(option.label)}`}
            </div>
         </li>
      `;
   }

   renderSelectIcon() {
      if (!this.selectIconName) return nothing;

      return html`
         <i
            class=${`fa-solid fa-${this.selectIconName}`}
            tabindex="0"
            style="font-size: 1rem; position: absolute; top: 11px; right: 15px;"
         ></i>
      `;
   }

   renderClearIcon() {
      if (this.selectInactive) return nothing;

      return html`
         <i
            class="fa-solid fa-times"
            style="font-size: 1rem; position: absolute; top: 11px; right: 45px; cursor: pointer;"
            tabindex="0"
            @click=${() => this.handleClear()}
         >
         </i>
      `;
   }

   renderInput() {
      const inputCss = classNames(
         'form-control',
         {
            'form-select': !(
               this.selectIconName && this.selectIconName?.length > 0
            ),
         },
         this.selectClassName,
         SizesValuesMap[this.size ?? Sizes.Medium]
      );

      if (this.selectValue) {
         return html`
                <div class="d-flex">
                    <input 
                        id=${this.internalId}
                        class=${inputCss}
                        title=${this.name}
                        type="text"
                        value=${this.selectValue}
                        disabled
                        tabindex="0"
                        ?aria-label=${this.ariaLabel}
                    >
                    </input>
                    ${this.renderClearIcon()}
                    ${this.renderSelectIcon()}
                </div>
            `;
      }

      return html`
            <input 
                class=${inputCss}
                title=${this.name}
                type="text"
                .value="${this.value ?? ''}"
                ?disabled=${this.selectInactive === true}
                @click=${this._handleShowPreviousSearch}
                @keyup=${this._handleKeyUp}
                placeholder=${this.placeholder ?? 'Please select...'}
                tabIndex="0"
                ?aria-label=${this.ariaLabel}
                id=${this.internalId}
            >
            </input>
            ${this.renderSelectIcon()}
        `;
   }

   render() {
      return html`
         <div class="dropdown">
            <span
               id="atl-drop-down-menu-cont"
               data-bs-toggle="dropdown"
               aria-haspopup="true"
               aria-label="avatar-button-label"
               data-type="user-profile-menu-toggle"
            >
               ${this.renderInput()}
            </span>
            <ul
               class=${classNames(
                  'dropdown-menu py-0 border border-secondary rounded w-100',
                  this.listClassName,
                  { show: this.toggled }
               )}
               data-bs-popper="none"
               data-type="user-profile-menu-dropdown"
            >
               ${this.renderOptionsToShow()}
            </ul>
         </div>
      `;
   }

   private _handleShowPreviousSearch = () => {
      if (
         !this.showPreviousSearchHistory ||
         (this.value && this.value.length > 0)
      )
         return;

      this.showingPreviousSearch = true;
      this._setEventListener();
      this._setStartSearchValues();
      this.dispatchEvent(new AtlSelectPreviousSearchEvent());
   };

   private _handleKeyUp = (
      event: KeyboardEvent & { target: HTMLInputElement }
   ) => {
      const newValue = (event.target.value as string).trim();
      this.value = newValue;
      if (
         event.key === ' ' ||
         (event.key.length !== 1 &&
            event.key !== 'Backspace' &&
            event.key !== 'Delete')
      )
         return;

      if (newValue.length === 0 && this.showPreviousSearchHistory)
         return this._handleShowPreviousSearch();

      this._handleOnChange(newValue);
   };

   private _handleOnChange = (value: string) => {
      this.showingPreviousSearch = false;
      if (value?.length > (this.minimumCharacterLength ?? 0)) {
         this._setEventListener();
         this._setStartSearchValues();
         this.dispatchEvent(new AtlSelectKeyDownEvent(value));
      } else {
         this.options = undefined;
         this.toggled = false;
      }
   };

   private _handleSelection = (value: string) => {
      if (this.showingPreviousSearch) {
         this.value = value;
      }
      const selectedValue =
         (this.options && this.options.find((o) => o.value === value)) ??
         undefined;

      if (this.clearSearchValue) {
         this.value = '';
      }
      if (!this.showToggleOnSelection || this.options?.length === 1) {
         this.toggled = false;
      }
      this.dispatchEvent(new AtlSelectChangeEvent(selectedValue));
   };

   private _handleItemKeyDown = (e: KeyboardEvent, value: string) => {
      if (e.key === 'Enter') {
         this._handleSelection(value);
      }
   };

    private handleClear = () => {
      this.value = '';
      this.dispatchEvent(new AtlSelectChangeEvent());
   };

   private _setEventListener = () => {
      window.addEventListener('click', (e: MouseEvent) => {
         const target = e.target as HTMLElement;
         if (target?.nodeName !== 'ATLANTIS-SELECT-ASYNC') {
            this.toggled = false;
            this.showingPreviousSearch = false;
            window.removeEventListener('click', () => {});
         }
      });
   };

   private _setStartSearchValues = () => {
      this.options = undefined;
      this.toggled = true;
      this.loading = true;
   };
}
